import { useCallback, useMemo, useState } from 'react';

import { useActivityBuilderPresentContext } from '../../present/activity-builder-present.context';
import { useGetStudentAnswersPollingFallback } from '../use-get-student-answers';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { ComponentMultipleChoice } from '../../components/component.constants';
import MultipleChoiceSelectedAnswers from '../../components/multiple-choice/multiple-choice-selected-answers';
import { Student, StudentProfile } from '../classroom.constants';
import ClassroomAnswerDetailHeader from '../classroom-answer-detail-header';
import ClassroomAwaitingResponse from '../classroom-awaiting';
import type { MultipleChoiceAnswerFragment } from '../../present/activity-answers.graphql.generated';
import { ClassroomAttemptNav } from '../classroom-attempt';
import { AttemptContent } from '../classroom-attempt.style';
import SubActivityView from 'approot/activity-builder/components/sub-activity/sub-activity-view';

type Props = {
  student: StudentProfile;
  studentsInClass: Student[];
  component: Omit<ComponentMultipleChoice, '__typename'>;
  onSelect: (id: number) => void;
};

const MultipleChoiceAttempts = ({
  student,
  studentsInClass,
  component,
  onSelect,
}: Props) => {
  const [index, setIndex] = useState<number>(0);

  const { userClassId } = useActivityBuilderPresentContext();

  const { data } = useGetStudentAnswersPollingFallback({
    userClassId: Number(userClassId),
    componentId: component.id,
    studentId: student.studentId,
  });

  const answers = useMemo(() => data?.getClassStudentsAnswers || [], [data]);

  const currentAnswer = useMemo(() => {
    return answers[index]?.answer as MultipleChoiceAnswerFragment;
  }, [answers, index]);

  const handlePrev = useCallback(() => {
    setIndex(index - 1);
  }, [index]);

  const handleNext = useCallback(() => {
    setIndex(index + 1);
  }, [index]);

  return (
    <>
      <ClassroomAnswerDetailHeader
        studentId={student.studentId}
        students={studentsInClass}
        onSelect={onSelect}
      />
      <SubActivityView subActivityDetails={component.subActivityProperties}>
        <AttemptContent>
          {!data ? (
            <Bouncer />
          ) : (
            <>
              {currentAnswer ? (
                <MultipleChoiceSelectedAnswers
                  {...component}
                  isResponsive
                  showAnswer
                  selectedAnswers={currentAnswer}
                />
              ) : (
                <ClassroomAwaitingResponse />
              )}
            </>
          )}
        </AttemptContent>
        {answers.length > 1 && (
          <ClassroomAttemptNav
            index={index}
            total={answers.length}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        )}
      </SubActivityView>
    </>
  );
};

export default MultipleChoiceAttempts;
